import { PackageMinus, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { DisposeStockModal } from '../components/DisposeStockModal';
import { StockBatchModal } from '../components/StockBatchModal';
import { inventoryItemsApi, storageFacilitiesApi } from '../inventoryApi';
import type { InventoryItemOption } from '../inventoryTypes';
import { stockBatchesApi } from '../stockApi';
import type { StockBatchDetail, StockBatchListItem } from '../stockTypes';
import { BATCH_STATUS, BATCH_STATUS_LABEL } from '../stockTypes';

/**
 * Lots of quantity-tracked stock, each sitting at one facility.
 *
 * **The anchor is the BATCH NUMBER** — what the supplier stamped on the box and what a recall
 * traceback quotes. Unlike the classifying field the anchor rule usually warns about, it does
 * not title every row alike. It is deliberately not merged with the item into a two-value
 * anchor: that exception is for a row no single value identifies, and a lot number identifies
 * one. The item keeps its own sortable column immediately beside it, which is also the answer
 * to the one weakness — batch numbers carry no unique index, so two suppliers may legitimately
 * stamp the same one.
 */
export function StockBatchesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<StockBatchDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [disposing, setDisposing] = useState<StockBatchListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    batch: StockBatchListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    A lot cannot be received without an item and a facility, both required on the form, so the
    shell states what is missing before the reader opens a form they could not submit.

    Seeded TRUE and left true on a failed feed: flashing "add an item first" on every load of a
    working module is worse than a banner arriving a moment late, and a request that did not
    come back is not evidence that a catalog is empty.

    <b>Both feeds are permission-gated here, unlike the item page's.</b> This module's implied
    reads are SCOPED to create and update, so a reader holding only `stock-batches.read`
    genuinely lacks them — asking would 403. The banner is about what a CREATE needs, and a
    reader who cannot create does not need it.
  */
  const [ready, setReady] = useState({ items: true, facilities: true });

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      inventoryItemsApi.options(controller.signal),
      storageFacilitiesApi.options(controller.signal),
    ])
      .then(([items, facilities]) => {
        if (controller.signal.aborted) return;

        setReady({
          // A SERIALIZED item cannot take a batch, so the question is not "are there items"
          // but "is there one this form could accept" — the server refuses the rest.
          items: items.some((i: InventoryItemOption) => !i.tracksSerial),
          facilities: facilities.length > 0,
        });
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchBatches = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      stockBatchesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          item: query.facets.item?.join(','),
          facility: query.facets.facility?.join(','),
          condition: query.facets.condition?.join(','),
        },
        signal,
      ),
    [],
  );

  // The list row carries only what a list needs, so the editor fetches the whole record first —
  // a form seeded from the row would drop the quarantine reason and the notes, and save would
  // clear them.
  const openEditor = useCallback(async (batch: StockBatchListItem) => {
    setError(null);

    try {
      setEditing(await stockBatchesApi.get(batch.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not open this batch.', 0));
    }
  }, []);

  const rowActions = useCallback(
    (batch: StockBatchListItem): MenuItem[] => [
      {
        key: `edit-${batch.id}`,
        label: 'Edit',
        permission: 'stock-batches.update',
        onClick: () => void openEditor(batch),
      },
      {
        /*
          Its OWN key, never `update`. Destroying stock and correcting a note are not the same
          privilege, and this is the one batch operation that cannot be undone by editing the
          row back.
        */
        key: `dispose-${batch.id}`,
        label: 'Dispose',
        icon: <PackageMinus className="h-4 w-4" aria-hidden="true" />,
        permission: 'stock-batches.dispose',
        disabledReason:
          batch.quantityOnHand > 0 ? null : 'This lot holds nothing left to dispose of.',
        onClick: () => setDisposing(batch),
      },
      {
        key: `restore-${batch.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'stock-batches.delete',
        disabledReason: batch.isActive ? 'This batch is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', batch }),
      },
    ],
    [openEditor],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await stockBatchesApi.deactivate(confirming.batch.id);
      else await stockBatchesApi.activate(confirming.batch.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<StockBatchListItem>
        module="stock-batches"
        breadcrumb={['Logistics', 'Inventory', 'Stock Batches']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'condition', label: 'Condition' },

          /*
            GATED, and unlike the item list's category facet these gates can genuinely hide
            something: this module's implied reads are scoped to the write actions, so a
            read-only reader does not hold them. The COLUMNS stay ungated — a lot's SKU and
            facility say what the row in front of them IS, and a list that could not name its
            own item would be unreadable.
          */
          { key: 'item', label: 'Item', permission: 'inventory-items.read' },
          { key: 'facility', label: 'Facility', permission: 'storage-facilities.read' },
        ]}
        prerequisites={[
          {
            label: 'a quantity-tracked item',
            met: ready.items,
            to: '/inventory/settings/items',
            permission: 'inventory-items.read',
          },
          {
            label: 'a storage facility',
            met: ready.facilities,
            to: '/inventory/settings/storage-facilities',
            permission: 'storage-facilities.read',
          },
        ]}
        /*
          BOTH types, because one batch operation writes two rows. The ledger line is what
          records where stock went, so a module history blind to `StockMovement` would show the
          batch changing and never say why.
        */
        auditEntityTypes={['StockBatch', 'StockMovement']}
        rowKey={(batch) => batch.id}
        fetch={fetchBatches}
        reloadKey={reloadKey}
        searchPlaceholder="Search batch number, SKU, item or facility"
        emptyMessage="No stock batches yet. Receive a lot to start tracking it."
        onCreate={() => setCreating(true)}
        createPermission="stock-batches.create"
        onRowOpen={(batch) => navigate(`/inventory/stock-batches/${batch.id}`)}
        openPermission="stock-batches.read"
        rowActions={rowActions}
        onDelete={(batch) => setConfirming({ kind: 'deactivate', batch })}
        deletePermission="stock-batches.delete"
        deleteLabel={() => 'Delete'}
        /*
          Disabled for STATE, with the quantity in the reason. The server refuses this too — the
          rule lives there — but meeting it as a 409 after a password prompt is a worse way to
          learn it than a disabled entry that says why and what to do instead.
        */
        deleteDisabledReason={(batch) =>
          !batch.isActive
            ? 'This batch is already deleted.'
            : batch.quantityOnHand > 0
              ? `This lot still holds ${batch.quantityOnHand}. Dispose of the stock, or correct the quantity to zero, before deleting the record.`
              : null
        }
        onClearData={() => stockBatchesApi.clear()}
        clearDataPermission="stock-batches.clear-data"
        moduleNoun="stock batches"
        otherInfo={(batch) => batch}
      />

      {creating || editing !== null ? (
        <StockBatchModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {disposing ? (
        <DisposeStockModal
          open
          batch={disposing}
          onClose={() => setDisposing(null)}
          onSaved={() => {
            setDisposing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete batch' : 'Restore batch'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring puts something back.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`stock-batch-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Lot "${confirming.batch.batchNumber}" stops counting toward stock on hand.`
                  : `Lot "${confirming.batch.batchNumber}" counts toward stock on hand again.`}
              </p>

              <p>
                The ledger is untouched either way — a movement is never deleted, so the history
                of this lot survives whatever happens to the row.
              </p>

              <p>Nothing is erased. A deleted batch can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * The condition tone.
 *
 * **No amber**, which is the index-page rule rather than a palette preference: amber is the
 * brand colour here, so on a row it reads as chrome the shell put there rather than as a fact
 * about the lot. Anything that stops stock being issued is `blocked`; a lot that is simply
 * empty is `inactive`.
 */
function conditionTone(status: StockBatchListItem['status']) {
  if (status === BATCH_STATUS.active) return 'active' as const;
  if (status === BATCH_STATUS.depleted) return 'inactive' as const;
  return 'blocked' as const;
}

const COLUMNS: ColumnDef<StockBatchListItem>[] = [
  {
    key: 'batchNumber',
    header: 'Batch',
    width: 170,
    sortable: true,
    render: (batch) => <TextCell value={batch.batchNumber} mono />,
  },
  {
    key: 'sku',
    header: 'SKU',
    width: 150,
    sortable: true,
    summary: true,
    render: (batch) => <TextCell value={batch.sku} mono />,
  },
  {
    key: 'itemName',
    header: 'Item',
    width: 240,
    flex: true,
    sortable: true,
    summary: true,
    render: (batch) => <TextCell value={batch.itemName} />,
  },
  {
    key: 'storageFacilityName',
    header: 'Facility',
    width: 180,
    sortable: true,
    summary: true,
    render: (batch) => <TextCell value={batch.storageFacilityName} />,
  },
  {
    key: 'quantityOnHand',
    header: 'On hand',
    width: 110,
    align: 'right',
    sortable: true,
    // `numeric`, so a lot that has run down to nothing dims rather than competing with the
    // quantities worth reading.
    render: (batch) => <TextCell value={String(batch.quantityOnHand)} mono numeric />,
  },
  {
    key: 'quantityReceived',
    header: 'Received',
    width: 110,
    align: 'right',
    render: (batch) => <TextCell value={String(batch.quantityReceived)} mono numeric />,
  },
  {
    key: 'unitCode',
    header: 'Unit',
    width: 80,
    render: (batch) => <TextCell value={batch.unitCode} mono />,
  },
  {
    key: 'expiryDate',
    header: 'Expires',
    width: 130,
    sortable: true,
    /*
      A CALENDAR date, so it goes through `formatDateOnly` and a `<TextCell mono />` rather than
      `<DateCell>`. A lot expires on a DAY: putting it through the instant path would tell a
      reader west of UTC that stock lapsed the day before it did.
    */
    render: (batch) => (
      <TextCell value={batch.expiryDate === null ? null : formatDateOnly(batch.expiryDate)} mono />
    ),
  },
  {
    key: 'unitCost',
    header: 'Unit cost',
    width: 130,
    align: 'right',
    render: (batch) => (
      <TextCell value={batch.unitCost === null ? null : formatMoney(batch.unitCost)} mono numeric />
    ),
  },
  {
    /*
      The lot's CONDITION, which is a different axis from whether the row is deleted — so it is
      an ordinary column and `status: true` belongs to the one below it. Expired arrives here
      DERIVED: nothing wrote it, and the facet filters on the same computation, so a tab can
      never list a row whose own pill contradicts it.
    */
    key: 'condition',
    header: 'Condition',
    width: 130,
    render: (batch) => (
      <StatusText tone={conditionTone(batch.status)}>{BATCH_STATUS_LABEL[batch.status]}</StatusText>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (batch) =>
      batch.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
